export function PrivacyPolicy() {
  return (
    <div className="flex flex-col items-center bg-gray-50 p-4 max-w-4xl mx-auto">
      <h1 className="font-bold text-3xl text-gray-800 mb-6">Privacy Policy</h1>

      <div className="w-full bg-white rounded-lg shadow-md p-6 space-y-6 text-gray-700">
        <div>
          <p className="text-sm text-gray-500 mb-4">
            Last Updated: January 1, 2026
          </p>

          <p className="mb-4">
            Welcome to Tæmú Shop. We respect your privacy and are committed to
            protecting your personal data. This privacy policy will inform you
            about how we look after your personal data when you visit our
            website and tell you about your privacy rights and how the law
            protects you.
          </p>
        </div>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            1. Information We Collect
          </h2>
          <div className="space-y-3">
            <h3 className="font-medium text-lg text-gray-700">
              Personal Information
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Name and contact information (email, phone, address)</li>
              <li>Payment and billing information</li>
              <li>Order history and preferences</li>
              <li>Account credentials</li>
            </ul>

            <h3 className="font-medium text-lg text-gray-700 mt-4">
              Technical Information
            </h3>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>IP address and browser information</li>
              <li>Cookies and tracking data</li>
              <li>Usage patterns and preferences</li>
              <li>Device information</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            2. How We Use Your Information
          </h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>To process and fulfill your orders</li>
            <li>To provide customer service and support</li>
            <li>To send order confirmations and shipping updates</li>
            <li>To improve our website and services</li>
            <li>To send promotional offers (with your consent)</li>
            <li>To prevent fraud and ensure security</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            3. Information Sharing
          </h2>
          <p className="mb-3">We may share your personal information with:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Service Providers:</strong> Payment processors, shipping
              companies, email services
            </li>
            <li>
              <strong>Business Partners:</strong> Trusted third parties who help
              us operate our business
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              protect our rights
            </li>
            <li>
              <strong>Business Transfers:</strong> In case of merger,
              acquisition, or asset sale
            </li>
          </ul>
          <p className="mt-3">
            We do not sell, trade, or rent your personal information to third
            parties for marketing purposes.
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            4. Data Security
          </h2>
          <p className="mb-3">
            We implement appropriate security measures to protect your personal
            information against unauthorized access, alteration, disclosure, or
            destruction. These include:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>SSL encryption for data transmission</li>
            <li>Secure payment processing</li>
            <li>Regular security audits</li>
            <li>Access controls and employee training</li>
            <li>Data backup and recovery procedures</li>
          </ul>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            5. Cookies and Tracking
          </h2>
          <p className="mb-3">
            We use cookies and similar technologies to enhance your browsing
            experience. Types of cookies we use:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Essential Cookies:</strong> Required for website
              functionality
            </li>
            <li>
              <strong>Performance Cookies:</strong> Help us analyze website
              usage
            </li>
            <li>
              <strong>Functional Cookies:</strong> Remember your preferences
            </li>
            <li>
              <strong>Marketing Cookies:</strong> Show relevant advertisements
              (with consent)
            </li>
          </ul>
          <p className="mt-3">
            You can manage cookie preferences through your browser settings.
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            6. Your Rights
          </h2>
          <p className="mb-3">You have the right to:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Access your personal data</li>
            <li>Correct inaccurate information</li>
            <li>Request deletion of your data</li>
            <li>Object to processing</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p className="mt-3">
            To exercise these rights, please contact us at{' '}
            <a
              href="mailto:privacy@tæmu.com"
              className="text-amber-600 hover:text-amber-700 transition-colors"
            >
              privacy@tæmu.com
            </a>
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            7. Data Retention
          </h2>
          <p>
            We retain your personal information only as long as necessary for
            the purposes outlined in this policy or as required by law. Order
            information is typically retained for 7 years for tax and accounting
            purposes.
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            8. International Transfers
          </h2>
          <p>
            Your information may be transferred to and processed in countries
            other than your country of residence. We ensure appropriate
            safeguards are in place to protect your data during such transfers.
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            9. Children's Privacy
          </h2>
          <p>
            Our website is not intended for children under 13 years of age. We
            do not knowingly collect personal information from children under
            13. If you believe we have collected such information, please
            contact us immediately.
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            10. Changes to This Policy
          </h2>
          <p>
            We may update this privacy policy from time to time. We will notify
            you of any changes by posting the new policy on this page and
            updating the "Last Updated" date. We encourage you to review this
            policy periodically.
          </p>
        </section>

        <section>
          <h2 className="font-semibold text-xl text-gray-800 mb-3">
            11. Contact Information
          </h2>
          <div className="bg-gray-50 p-4 rounded-md">
            <p className="mb-2">
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <div className="space-y-1">
              <p>
                <strong>Email:</strong>{' '}
                <a
                  href="mailto:privacy@tæmu.com"
                  className="text-amber-600 hover:text-amber-700 transition-colors"
                >
                  privacy@tæmu.com
                </a>
              </p>
              <p>
                <strong>Phone:</strong>{' '}
                <a
                  href="tel:+4754321016"
                  className="text-amber-600 hover:text-amber-700 transition-colors"
                >
                  +47 54 32 10 16
                </a>
              </p>
              <p>
                <strong>Address:</strong> Tæmú Shop, Karl Johans Gate 22, 0954
                Oslo, Norway
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
